import { Course } from '@/types/courseTypes';

export const terraformCourse: Course = {
  id: 'terraform-iac',
  title: 'Infrastructure as Code with Terraform',
  description: 'Master Infrastructure as Code using Terraform. Learn to provision, manage, and version cloud infrastructure declaratively.',
  level: 'Intermediate',
  totalDuration: '8 hours',
  image: '/api/placeholder/400/300',
  prerequisites: ['Basic cloud knowledge', 'Command line familiarity'],
  learningOutcomes: [
    'Design infrastructure using Terraform HCL',
    'Manage state files and remote backends',
    'Implement infrastructure versioning and rollbacks',
    'Build reusable modules and best practices'
  ],
  modules: [
    {
      id: 'terraform-fundamentals',
      title: 'Terraform Fundamentals',
      description: 'Introduction to Infrastructure as Code and Terraform basics',
      content: `# Terraform Fundamentals - Infrastructure as Code Revolution

## The Infrastructure Challenge

### Traditional Infrastructure Problems
- **Manual Configuration**: Error-prone, time-consuming manual setup
- **Configuration Drift**: Environments diverge over time
- **No Version Control**: Can't track infrastructure changes
- **Scaling Issues**: Difficult to replicate environments
- **Documentation**: Infrastructure setup often undocumented

### Infrastructure as Code Benefits
- **Reproducibility**: Identical infrastructure every time
- **Version Control**: Track all infrastructure changes
- **Automation**: Eliminate manual configuration errors
- **Scalability**: Easily create multiple environments
- **Cost Management**: Better resource tracking and optimization

## Terraform Core Concepts

### The Terraform Workflow
\`\`\`bash
# 1. Write - Define infrastructure in .tf files
# 2. Plan - Preview changes before applying
terraform plan

# 3. Apply - Create/modify infrastructure
terraform apply

# 4. Destroy - Clean up resources
terraform destroy
\`\`\`

### HashiCorp Configuration Language (HCL)
\`\`\`hcl
# Basic syntax example
terraform {
  required_version = ">= 1.0"
  required_providers {
    aws = {
      source  = "hashicorp/aws"
      version = "~> 5.0"
    }
  }
}

# Provider configuration
provider "aws" {
  region = var.aws_region
  
  default_tags {
    tags = {
      Environment = var.environment
      Project     = var.project_name
      ManagedBy   = "Terraform"
    }
  }
}

# Variables
variable "aws_region" {
  description = "AWS region for resources"
  type        = string
  default     = "us-west-2"
}

variable "environment" {
  description = "Environment name"
  type        = string
  validation {
    condition = contains(["dev", "staging", "prod"], var.environment)
    error_message = "Environment must be dev, staging, or prod."
  }
}

# Data sources
data "aws_availability_zones" "available" {
  state = "available"
}

# Resources
resource "aws_vpc" "main" {
  cidr_block           = "10.0.0.0/16"
  enable_dns_hostnames = true
  enable_dns_support   = true
  
  tags = {
    Name = "\${var.project_name}-\${var.environment}-vpc"
  }
}

resource "aws_subnet" "public" {
  count = 2
  
  vpc_id                  = aws_vpc.main.id
  cidr_block              = "10.0.\${count.index + 1}.0/24"
  availability_zone       = data.aws_availability_zones.available.names[count.index]
  map_public_ip_on_launch = true
  
  tags = {
    Name = "\${var.project_name}-\${var.environment}-public-\${count.index + 1}"
    Type = "Public"
  }
}

# Outputs
output "vpc_id" {
  description = "ID of the VPC"
  value       = aws_vpc.main.id
}

output "public_subnet_ids" {
  description = "IDs of public subnets"
  value       = aws_subnet.public[*].id
}
\`\`\`

## Advanced Terraform Features

### Local Values and Functions
\`\`\`hcl
locals {
  # Common tags applied to all resources
  common_tags = {
    Environment   = var.environment
    Project      = var.project_name
    Owner        = var.owner
    CreatedBy    = "Terraform"
    CreatedDate  = formatdate("YYYY-MM-DD", timestamp())
  }
  
  # Dynamic naming
  name_prefix = "\${var.project_name}-\${var.environment}"
  
  # Conditional logic
  instance_type = var.environment == "prod" ? "t3.large" : "t3.micro"
  
  # Complex data transformation
  subnet_config = {
    for i, az in data.aws_availability_zones.available.names : az => {
      cidr_block = "10.0.\${i + 10}.0/24"
      az         = az
    }
  }
}
\`\`\`

### Conditional Resources and Expressions
\`\`\`hcl
# Conditional resource creation
resource "aws_instance" "web" {
  count = var.create_web_server ? 1 : 0
  
  ami           = data.aws_ami.amazon_linux.id
  instance_type = local.instance_type
  subnet_id     = aws_subnet.public[0].id
  
  # Conditional security group assignment
  vpc_security_group_ids = var.enable_ssh ? [
    aws_security_group.web.id,
    aws_security_group.ssh[0].id
  ] : [aws_security_group.web.id]
  
  user_data = templatefile("\${path.module}/user_data.sh", {
    environment = var.environment
    app_name    = var.app_name
  })
  
  tags = merge(local.common_tags, {
    Name = "\${local.name_prefix}-web-server"
    Role = "WebServer"
  })
}

# Conditional security group for SSH
resource "aws_security_group" "ssh" {
  count = var.enable_ssh ? 1 : 0
  
  name_prefix = "\${local.name_prefix}-ssh"
  vpc_id      = aws_vpc.main.id
  
  ingress {
    from_port   = 22
    to_port     = 22
    protocol    = "tcp"
    cidr_blocks = var.ssh_allowed_cidrs
  }
  
  tags = merge(local.common_tags, {
    Name = "\${local.name_prefix}-ssh-sg"
  })
}
\`\`\`

## Terraform Best Practices

### File Organization
\`\`\`
terraform-project/
├── main.tf              # Main configuration
├── variables.tf         # Input variables
├── outputs.tf          # Output values
├── versions.tf         # Provider requirements
├── terraform.tfvars   # Variable values (don't commit sensitive values)
├── modules/           # Local modules
│   └── networking/
│       ├── main.tf
│       ├── variables.tf
│       └── outputs.tf
└── environments/      # Environment-specific configs
    ├── dev/
    │   └── terraform.tfvars
    ├── staging/
    │   └── terraform.tfvars
    └── prod/
        └── terraform.tfvars
\`\`\`

### State Management
\`\`\`hcl
# Backend configuration for remote state
terraform {
  backend "s3" {
    bucket         = "my-terraform-state-bucket"
    key            = "networking/terraform.tfstate"
    region         = "us-west-2"
    encrypt        = true
    dynamodb_table = "terraform-state-locks"
  }
}
\`\`\`

### Error Handling and Validation
\`\`\`hcl
# Input validation
variable "instance_count" {
  description = "Number of instances to create"
  type        = number
  
  validation {
    condition     = var.instance_count >= 1 && var.instance_count <= 10
    error_message = "Instance count must be between 1 and 10."
  }
}

# Lifecycle management
resource "aws_instance" "app" {
  ami           = data.aws_ami.app.id
  instance_type = var.instance_type
  
  # Prevent accidental termination
  lifecycle {
    prevent_destroy = true
    
    # Create new instance before destroying old one
    create_before_destroy = true
    
    # Ignore changes to certain attributes
    ignore_changes = [
      ami,
      user_data
    ]
  }
}
\`\`\``,
      order: 1,
      estimatedTime: '2 hours',
      duration: '2 hours',
      difficulty: 'beginner',
      objectives: [
        'Understand Infrastructure as Code principles',
        'Set up Terraform development environment',
        'Write your first Terraform configuration'
      ]
    },
    {
      id: 'terraform-providers-resources',
      title: 'Providers and Resources',
      description: 'Working with Terraform providers and resource management',
      content: 'Deep dive into Terraform providers and how to manage cloud resources effectively.',
      order: 2,
      estimatedTime: '2 hours',
      duration: '2 hours',
      difficulty: 'intermediate',
      objectives: [
        'Configure and use Terraform providers',
        'Manage resource lifecycle',
        'Handle resource dependencies'
      ]
    },
    {
      id: 'terraform-state-management',
      title: 'State Management',
      description: 'Managing Terraform state files and remote backends',
      content: 'Learn state management strategies, remote backends, and collaboration workflows.',
      order: 3,
      estimatedTime: '2 hours',
      duration: '2 hours',
      difficulty: 'intermediate',
      objectives: [
        'Understand Terraform state concepts',
        'Configure remote state backends',
        'Implement state locking and team workflows'
      ]
    },
    {
      id: 'terraform-modules-best-practices',
      title: 'Modules and Best Practices',
      description: 'Building reusable modules and following Terraform best practices',
      content: 'Create reusable Terraform modules and implement production-ready patterns.',
      order: 4,
      estimatedTime: '2 hours',
      duration: '2 hours',
      difficulty: 'advanced',
      objectives: [
        'Design and build Terraform modules',
        'Implement testing strategies',
        'Follow security and operational best practices'
      ]
    }
  ]
};

export const ansibleCourse: Course = {
  id: 'ansible-automation',
  title: 'Automation with Ansible',
  description: 'Learn configuration management and application deployment automation using Ansible.',
  level: 'Intermediate',
  totalDuration: '6 hours',
  image: '/api/placeholder/400/300',
  prerequisites: ['Linux basics', 'SSH knowledge'],
  learningOutcomes: [
    'Write Ansible playbooks for automation',
    'Manage inventory and variables',
    'Deploy applications with Ansible',
    'Implement CI/CD with Ansible'
  ],
  modules: [
    {
      id: 'ansible-basics',
      title: 'Ansible Fundamentals',
      description: 'Introduction to Ansible and automation concepts',
      content: 'Learn Ansible architecture, inventory management, and basic ad-hoc commands.',
      order: 1,
      estimatedTime: '1.5 hours',
      duration: '1.5 hours',
      difficulty: 'beginner',
      objectives: [
        'Understand Ansible architecture',
        'Set up Ansible control node',
        'Run ad-hoc commands'
      ]
    },
    {
      id: 'ansible-playbooks',
      title: 'Playbooks and Tasks',
      description: 'Writing and executing Ansible playbooks',
      content: 'Master playbook structure, tasks, handlers, and conditional execution.',
      order: 2,
      estimatedTime: '2 hours',
      duration: '2 hours',
      difficulty: 'intermediate',
      objectives: [
        'Write structured playbooks',
        'Use variables and templates',
        'Implement error handling'
      ]
    },
    {
      id: 'ansible-roles-galaxy',
      title: 'Roles and Galaxy',
      description: 'Organizing code with roles and using Ansible Galaxy',
      content: 'Learn to structure automation code using roles and leverage community collections.',
      order: 3,
      estimatedTime: '1.5 hours',
      duration: '1.5 hours',
      difficulty: 'intermediate',
      objectives: [
        'Create and use Ansible roles',
        'Work with Ansible Galaxy',
        'Manage dependencies'
      ]
    },
    {
      id: 'ansible-cicd-deployment',
      title: 'CI/CD and Deployment',
      description: 'Integrating Ansible with CI/CD pipelines',
      content: 'Implement automated deployment pipelines using Ansible with various CI/CD tools.',
      order: 4,
      estimatedTime: '1 hour',
      duration: '1 hour',
      difficulty: 'advanced',
      objectives: [
        'Integrate Ansible with CI/CD',
        'Implement blue-green deployments',
        'Monitor and troubleshoot deployments'
      ]
    }
  ]
};

export const k8sAdvancedCourse: Course = {
  id: 'kubernetes-advanced',
  title: 'Advanced Kubernetes Operations',
  description: 'Advanced Kubernetes cluster management, security, and production operations.',
  level: 'Advanced',
  totalDuration: '10 hours',
  image: '/api/placeholder/400/300',
  prerequisites: ['Kubernetes basics', 'Docker knowledge', 'YAML familiarity'],
  learningOutcomes: [
    'Design production-ready Kubernetes clusters',
    'Implement security and RBAC',
    'Set up monitoring and logging',
    'Manage stateful applications and storage'
  ],
  modules: [
    {
      id: 'k8s-cluster-architecture',
      title: 'Cluster Architecture & Setup',
      description: 'Production cluster architecture and high availability setup',
      content: 'Design and implement production-grade Kubernetes clusters with high availability.',
      order: 1,
      estimatedTime: '2.5 hours',
      duration: '2.5 hours',
      difficulty: 'advanced',
      objectives: [
        'Design HA cluster architecture',
        'Set up multi-master clusters',
        'Configure cluster networking'
      ]
    },
    {
      id: 'k8s-security-rbac',
      title: 'Security and RBAC',
      description: 'Implementing security controls and role-based access',
      content: 'Secure Kubernetes clusters with RBAC, network policies, and security contexts.',
      order: 2,
      estimatedTime: '2.5 hours',
      duration: '2.5 hours',
      difficulty: 'advanced',
      objectives: [
        'Configure RBAC policies',
        'Implement network security',
        'Set up pod security standards'
      ]
    },
    {
      id: 'k8s-storage-stateful',
      title: 'Storage and Stateful Applications',
      description: 'Managing persistent storage and stateful workloads',
      content: 'Handle persistent volumes, storage classes, and stateful application patterns.',
      order: 3,
      estimatedTime: '2.5 hours',
      duration: '2.5 hours',
      difficulty: 'advanced',
      objectives: [
        'Configure persistent storage',
        'Deploy stateful applications',
        'Manage data backup and recovery'
      ]
    },
    {
      id: 'k8s-monitoring-troubleshooting',
      title: 'Monitoring and Troubleshooting',
      description: 'Production monitoring, logging, and troubleshooting strategies',
      content: 'Implement comprehensive monitoring and develop troubleshooting skills for K8s.',
      order: 4,
      estimatedTime: '2.5 hours',
      duration: '2.5 hours',
      difficulty: 'advanced',
      objectives: [
        'Set up monitoring stack',
        'Implement centralized logging',
        'Troubleshoot cluster issues'
      ]
    }
  ]
};

export const INFRASTRUCTURE_COURSES = [
  terraformCourse,
  ansibleCourse,
  k8sAdvancedCourse
];